import { DEFECT_STATUSES } from '@felix/domain';
import { fromZonedInput, zonedInput } from '../../inputs/lt-utc.js';
import type { DefectCreateInput, DefectDetail, DefectOptions, DefectUpdateInput } from './types.js';

/**
 * DM-17 formunun durumu ve kayda dönüşü (ADR 0060): metin alanları boşsa `''`, seçimler `null`.
 * Kaydet yalnız değişen alanları gönderir (verilmeyen alan değişmez).
 */
export interface DefectFormState {
  kind: 'defect' | 'breakdown';
  equipmentId: string | null;
  systemName: string;
  department: string | null;
  location: string;
  jobDefinitionId: string | null;
  severity: 'minor' | 'major' | 'critical';
  priority: string | null;
  operationalImpact: boolean;
  description: string;
  /** LGS-17 Part A "Action required to rectify defect" (ADR 0096 §3). */
  actionRequired: string;
  immediateAction: string;
  captainActionOn: string | null;
  rectifiedOn: string | null;
  targetOn: string | null;
  reviewRequired: boolean | null;
  procedure: string;
  improvement: string;
  investigation: string;
  rootCause: string;
  correctiveAction: string;
  noRepairReason: string;
  testResult: string;
  conditionAfter: string | null;
  classNotificationRef: string;
  classNotificationWaiver: string;
  status: string;
  /** Yeni arızada bildirim günü ve saati (gemi saati). */
  reportedDay: string | null;
  reportedTime: string;
}

const s = (value: string | null | undefined) => value ?? '';

export function formOf(detail: DefectDetail): DefectFormState {
  return {
    kind: detail.kind === 'breakdown' ? 'breakdown' : 'defect',
    equipmentId: detail.equipmentId,
    systemName: s(detail.systemName),
    department: detail.department,
    location: s(detail.location),
    jobDefinitionId: detail.jobDefinitionId,
    severity: (detail.severity as DefectFormState['severity']) ?? 'major',
    priority: detail.priority,
    operationalImpact: detail.operationalImpact,
    description: detail.description,
    actionRequired: s(detail.actionRequired),
    immediateAction: s(detail.immediateAction),
    captainActionOn: detail.captainActionOn,
    rectifiedOn: detail.rectifiedOn,
    targetOn: detail.targetOn,
    reviewRequired: detail.procedureReview ? detail.procedureReview.required : null,
    procedure: s(detail.procedureReview?.procedure),
    improvement: s(detail.procedureReview?.improvement),
    investigation: s(detail.investigation),
    rootCause: s(detail.rootCause),
    correctiveAction: s(detail.correctiveAction),
    noRepairReason: s(detail.noRepairReason),
    testResult: s(detail.testResult),
    conditionAfter: detail.conditionAfter,
    classNotificationRef: s(detail.classNotificationRef),
    classNotificationWaiver: s(detail.classNotificationWaiver),
    status: detail.status,
    reportedDay: null,
    reportedTime: '',
  };
}

const FORM_DEPARTMENTS = new Set([
  'deck',
  'engine',
  'electrical',
  'navigation',
  'safety',
  'accommodation',
  'galley',
  'other',
]);

/**
 * Yeni arıza: ekipman kartından, günlük gözlemden (ADR 0060 §7), HSEQ raporundan (ADR 0096 §5)
 * ya da PMS bakım iş emrinden (§6) gelinmişse doldurulur.
 */
export function newForm(input: {
  now: string;
  tzOffsetMin: number;
  equipmentId: string | null;
  observation: DefectOptions['observation'];
  hseReport?: DefectOptions['hseReport'];
  sourceWorkOrder?: DefectOptions['sourceWorkOrder'];
}): DefectFormState {
  const local = zonedInput(input.now, input.tzOffsetMin);
  const observation = input.observation;
  const hse = input.hseReport ?? null;
  const wo = input.sourceWorkOrder ?? null;
  const department = observation?.department ?? hse?.department ?? null;
  const hseText = hse ? [hse.title, hse.description].filter(Boolean).join(' – ') : '';
  return {
    kind: 'defect',
    equipmentId: input.equipmentId ?? hse?.equipmentId ?? wo?.equipmentId ?? null,
    systemName: '',
    department: department && FORM_DEPARTMENTS.has(department) ? department : null,
    location: s(observation?.location ?? hse?.location),
    jobDefinitionId: wo?.jobDefinitionId ?? null,
    severity: 'major',
    priority: null,
    operationalImpact: false,
    description: s(observation?.summary) || hseText,
    actionRequired: '',
    immediateAction: '',
    captainActionOn: null,
    rectifiedOn: null,
    targetOn: null,
    reviewRequired: null,
    procedure: '',
    improvement: '',
    investigation: '',
    rootCause: '',
    correctiveAction: '',
    noRepairReason: '',
    testResult: '',
    conditionAfter: null,
    classNotificationRef: '',
    classNotificationWaiver: '',
    status: 'open',
    reportedDay: local.slice(0, 10),
    reportedTime: local.slice(11, 16),
  };
}

const text = (value: string) => (value.trim() === '' ? null : value.trim());

/** Yeni arızanın kaydı. */
export function createInputOf(
  form: DefectFormState,
  context: {
    tzOffsetMin: number;
    dailyReportId: string | null;
    hseReportId?: string | null;
    sourceWorkOrderId?: string | null;
  },
): DefectCreateInput {
  const reportedAt =
    form.reportedDay && form.reportedTime
      ? fromZonedInput(`${form.reportedDay}T${form.reportedTime}`, context.tzOffsetMin)
      : null;
  return {
    kind: form.kind,
    equipmentId: form.equipmentId,
    description: form.description.trim(),
    severity: form.severity,
    operationalImpact: form.operationalImpact,
    immediateAction: text(form.immediateAction),
    ...(reportedAt ? { reportedAt } : {}),
    department: form.department,
    location: text(form.location),
    systemName: form.equipmentId ? null : text(form.systemName),
    priority: form.priority,
    targetOn: form.targetOn,
    captainActionOn: form.captainActionOn,
    jobDefinitionId: form.jobDefinitionId,
    dailyReportId: context.dailyReportId,
    actionRequired: text(form.actionRequired),
    // Tek kaynak: gözlem, HSEQ raporu ya da bakım iş emri (ADR 0096 §5, §6)
    ...(context.dailyReportId === null && context.hseReportId
      ? { hseReportId: context.hseReportId }
      : {}),
    ...(context.dailyReportId === null && !context.hseReportId && context.sourceWorkOrderId
      ? { sourceWorkOrderId: context.sourceWorkOrderId }
      : {}),
  };
}

const TEXT_FIELDS = [
  'systemName',
  'location',
  'description',
  'actionRequired',
  'immediateAction',
  'investigation',
  'rootCause',
  'correctiveAction',
  'noRepairReason',
  'testResult',
  'classNotificationRef',
  'classNotificationWaiver',
] as const;
const VALUE_FIELDS = [
  'department',
  'jobDefinitionId',
  'severity',
  'priority',
  'operationalImpact',
  'captainActionOn',
  'rectifiedOn',
  'targetOn',
  'conditionAfter',
  'status',
] as const;

/** Kayıtlı arızaya göre değişen alanlar; değişiklik yoksa boş nesne. */
export function patchOf(form: DefectFormState, detail: DefectDetail): DefectUpdateInput {
  const base = formOf(detail);
  const patch: Record<string, unknown> = {};
  for (const key of TEXT_FIELDS) {
    if (form[key].trim() !== base[key].trim()) patch[key] = text(form[key]);
  }
  for (const key of VALUE_FIELDS) {
    if (form[key] !== base[key]) patch[key] = form[key];
  }
  if (form.equipmentId !== base.equipmentId && form.equipmentId !== null) {
    patch.equipmentId = form.equipmentId;
  }
  if (
    form.reviewRequired !== base.reviewRequired ||
    form.procedure.trim() !== base.procedure.trim() ||
    form.improvement.trim() !== base.improvement.trim()
  ) {
    patch.procedureReview =
      form.reviewRequired === null
        ? null
        : {
            required: form.reviewRequired,
            procedure: form.reviewRequired ? text(form.procedure) : null,
            improvement: form.reviewRequired ? text(form.improvement) : null,
          };
  }
  return patch as DefectUpdateInput;
}

/** Durum geçişleri (PMS belgesi §16): ileri ya da test/doğrulamadan onarıma dönüş. */
export function nextStatuses(current: string): string[] {
  const index = (DEFECT_STATUSES as readonly string[]).indexOf(current);
  return DEFECT_STATUSES.filter(
    (status, i) =>
      i >= index || (status === 'repair' && (current === 'testing' || current === 'verification')),
  );
}
